import type { ChartSection } from "@acto/genui-components-schema";
import {
  addDaysUtc,
  addMonthsUtc,
  isoToUtc,
  monthsBetweenUtc,
  quarterOfUtc,
  startOfMonthUtc,
  startOfQuarterUtc,
  toIsoDate,
} from "@acto/web-common/dates";
import {
  formatDayMonth,
  formatDayMonthRange,
  formatMonth,
  formatMonthYear,
} from "@acto/taskpane/field-sales-2/utils/format";

type Granularity = NonNullable<ChartSection["granularity"]>;

export interface ChartPeriod {
  axisLabel: string;
  tooltipLabel: string;
}

const MONTHS_PER_PERIOD: Record<Granularity, number> = { month: 1, quarter: 3 };

const resolveGranularity = (starts: Date[], granularity: ChartSection["granularity"]): Granularity => {
  if (granularity) return granularity;
  if (starts.length < 2) return "month";

  return monthsBetweenUtc(starts[starts.length - 2], starts[starts.length - 1]) === MONTHS_PER_PERIOD.quarter
    ? "quarter"
    : "month";
};

const periodStartOf = (date: Date, granularity: Granularity) =>
  granularity === "quarter" ? startOfQuarterUtc(date) : startOfMonthUtc(date);

const quarterLabel = (date: Date) => `Q${quarterOfUtc(date)}`;

/**
 * A row that starts inside its period is labelled with its start day instead of the period, and
 * its tooltip runs from that day to the end of the period.
 */
export function chartPeriods(
  starts: string[],
  granularity: ChartSection["granularity"],
  locale: string,
): ChartPeriod[] {
  const dates = starts.map(isoToUtc);
  const resolved = resolveGranularity(dates, granularity);

  return dates.map((date, index) => {
    const start = starts[index];
    const periodStart = periodStartOf(date, resolved);

    if (date.getTime() !== periodStart.getTime()) {
      const periodEnd = addDaysUtc(addMonthsUtc(periodStart, MONTHS_PER_PERIOD[resolved]), -1);
      return {
        axisLabel: formatDayMonth(start, locale),
        tooltipLabel: formatDayMonthRange({ start, end: toIsoDate(periodEnd) }, locale),
      };
    }

    if (resolved === "quarter") {
      return {
        axisLabel: quarterLabel(date),
        tooltipLabel: `${quarterLabel(date)} ${date.getUTCFullYear()}`,
      };
    }

    return { axisLabel: formatMonth(start, locale), tooltipLabel: formatMonthYear(start, locale) };
  });
}
